import { useEffect, useState } from "react";
import axios from "axios";

const PATH_TO_SLUG = {
  "/": "home",
  "/about-us": "about-us",
  "/contact-us": "contact-us",
  "/privacy-policy": "privacy-policy",
  "/terms-condition": "terms-condition",
  "/booking-terms": "booking-terms",
  "/thanks": "thanks",
  "/blog": "blog",
};

const HOME_SEO_FALLBACK = {
  title: "Druyp | Customize Your Short-Term Rental Stay",
  meta_description:
    "Book customizable short-term rentals with Druyp. Choose furniture, cleaning, and stays on your terms.",
  robots: "index, follow",
};

const withHomeSeoFallback = (slug, fields = {}) => {
  if (slug !== "home") {
    return fields;
  }

  return {
    ...HOME_SEO_FALLBACK,
    ...fields,
    title: fields.title?.trim() || HOME_SEO_FALLBACK.title,
    meta_description: fields.meta_description?.trim() || HOME_SEO_FALLBACK.meta_description,
    robots: fields.robots?.trim() || HOME_SEO_FALLBACK.robots,
  };
};

const resolveSeoSlug = (pathname) => {
  if (PATH_TO_SLUG[pathname]) {
    return PATH_TO_SLUG[pathname];
  }

  const propertyMatch = String(pathname || "").match(/^\/property_detail\/(\d+)\/?$/);
  if (propertyMatch) {
    return `property-${propertyMatch[1]}`;
  }

  return null;
};

const upsertMetaByName = (name, content) => {
  if (content === undefined || content === null || String(content).trim() === "") {
    return;
  }

  let element = document.querySelector(`meta[name="${name}"]`);

  if (!element) {
    element = document.createElement("meta");
    element.setAttribute("name", name);
    document.head.appendChild(element);
  }

  element.setAttribute("content", String(content));
};

const upsertCanonical = (href) => {
  if (!href) return;

  let element = document.querySelector('link[rel="canonical"]');

  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", "canonical");
    document.head.appendChild(element);
  }

  element.setAttribute("href", href);
};

const setHeadingText = (element, text) => {
  if (!element || !text) return;

  const richTarget = element.querySelector("span, p, [class*='heading']");
  if (richTarget) {
    richTarget.textContent = text;
    return;
  }

  element.textContent = text;
};

const applyHeadingFields = (fields = {}) => {
  const h1Nodes = Array.from(document.querySelectorAll("h1"));
  const h2Nodes = Array.from(document.querySelectorAll("h2"));

  if (fields.h1) {
    if (h1Nodes[0]) {
      setHeadingText(h1Nodes[0], fields.h1);
    } else if (h2Nodes[0]) {
      setHeadingText(h2Nodes[0], fields.h1);
    }
  }

  if (fields.h2) {
    if (h1Nodes[0] && h2Nodes[0]) {
      setHeadingText(h2Nodes[0], fields.h2);
    } else if (h2Nodes.length > 1) {
      setHeadingText(h2Nodes[1], fields.h2);
    } else if (h2Nodes[0] && !fields.h1) {
      setHeadingText(h2Nodes[0], fields.h2);
    }
  }
};

const applyDomFields = (fields = {}) => {
  applyHeadingFields(fields);

  if (fields.html_lang) {
    document.documentElement.setAttribute("lang", fields.html_lang);
  }

  if (fields.viewport) {
    upsertMetaByName("viewport", fields.viewport);
  }

  if (fields.anchor_href) {
    const anchor = document.querySelector("a[href]");
    if (anchor) {
      anchor.setAttribute("href", fields.anchor_href);
    }
  }
};

export const applyPageSeoFields = (fields = {}) => {
  if (fields.title) {
    document.title = fields.title;
  }

  if (fields.meta_description) {
    upsertMetaByName("description", fields.meta_description);
  }

  if (fields.robots) {
    upsertMetaByName("robots", fields.robots);
  }

  if (fields.canonical) {
    upsertCanonical(fields.canonical);
  }

  applyDomFields(fields);
};

/**
 * Loads PageSeoMetas for the current public route and applies them to head + headings.
 */
export const usePageSeo = (pathname) => {
  const [fields, setFields] = useState(null);
  const slug = resolveSeoSlug(pathname);

  useEffect(() => {
    if (!slug) return undefined;

    let cancelled = false;
    let retryTimer;

    const baseUrl = String(import.meta.env.VITE_APP_BASE_URL || "")
      .trim()
      .replace(/^["']|["']$/g, "")
      .replace(/\/+$/, "");

    const loadSeo = async () => {
      if (!baseUrl) {
        console.warn("[PageSeo] VITE_APP_BASE_URL is missing — SEO meta cannot load.");
        return;
      }

      try {
        const response = await axios.get(`${baseUrl}/api/page-seo/${slug}`);
        const rawFields = response?.data?.data?.fields;
        const nextFields =
          rawFields && typeof rawFields === "object"
            ? withHomeSeoFallback(slug, rawFields)
            : slug === "home"
              ? withHomeSeoFallback(slug, {})
              : null;

        if (cancelled || !nextFields) {
          return;
        }

        setFields(nextFields);
        applyPageSeoFields(nextFields);
        console.info("[PageSeo] Applied CMS SEO for", slug, nextFields);

        let attempts = 0;
        retryTimer = window.setInterval(() => {
          attempts += 1;
          applyDomFields(nextFields);
          if (attempts >= 8) {
            window.clearInterval(retryTimer);
          }
        }, 400);
      } catch (error) {
        console.warn("[PageSeo] Failed to load SEO meta for", slug, error?.message || error);
      }
    };

    void loadSeo();

    return () => {
      cancelled = true;
      if (retryTimer) {
        window.clearInterval(retryTimer);
      }
    };
  }, [slug]);

  return fields;
};

export default usePageSeo;
